import React from 'react';
import {
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	IconButton,
	MenuItem,
	Select,
	Tooltip,
} from '@mui/material';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import { useDispatch, useSelector } from 'react-redux';
import SearchField from '@/components/SearchField';
import { OwnerMultiSelect } from '@/components/selects/OwnerSelect';
import { BrokerGroupSelect } from '@/components/selects/BrokerGroupSelect';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	resetFilters,
	setBrokerGroupId,
	setIsPrimary,
	setOwnerIds,
} from '@/features/table/clientContactsPrefsSlice';
import { useGridItemProps } from '@/components/table/filterGrid';

const InsightContactsTableFilters = ({ isInitialLoading, searchParams = {} }) => {
	const {
		searchText,
		setSearchText,
		searchValue,
		setSearchValue,
		setPaginationModel,
		disabled: searchDisabled,
	} = searchParams;

	const dispatch = useDispatch();
	const filters = useSelector((state) => state.clientContactsPrefs);
	const { username: email } = useSelector((state) => state.msalAccount.account);
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const canViewGroup = userDetails?.canViewGroup;

	const { searchGridProps, generalGridProps, clearFilterGridProps } = useGridItemProps();

	const handleReset = () => {
		setSearchText('');
		setSearchValue('');
	};

	const handleSearch = () => {
		if (searchText != searchValue) {
			setPaginationModel((prev) => ({ ...prev, page: 0 }));
			setSearchValue(searchText);
		}
	};

	return (
		<Grid
			container
			spacing={1}
			sx={{
				width: '100%',
			}}
		>
			<Grid
				container
				spacing={2}
				width='100%'
				sx={{
					'& .MuiFormControl-root': {
						width: '100%',
					},
				}}
			>
				<Grid {...searchGridProps}>
					<FormControl fullWidth>
						<FormLabel>Search</FormLabel>
						<SearchField
							value={searchText}
							disabled={searchDisabled}
							onKeyDown={(e) => {
								if (e.key === 'Enter') {
									handleSearch();
								}
							}}
							onChange={(e) => setSearchText(e.target.value)}
							onClick={handleSearch}
							onReset={handleReset}
						/>
					</FormControl>
				</Grid>
				<Grid {...generalGridProps}>
					<FormControl fullWidth>
						<FormLabel>Main contact</FormLabel>
						<Select
							value={filters?.isPrimary ?? 'all'}
							disabled={isInitialLoading}
							onChange={(e) => dispatch(setIsPrimary(e.target.value))}
							displayEmpty
						>
							<MenuItem value='all'>All</MenuItem>
							<MenuItem value='yes'>Yes</MenuItem>
							<MenuItem value='no'>No</MenuItem>
						</Select>
						<FormHelperText>&nbsp;</FormHelperText>
					</FormControl>
				</Grid>
				{canViewGroup && (
					<>
						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>Client Owner</FormLabel>
								<OwnerMultiSelect
									users={filters?.ownerIds}
									disabled={isInitialLoading || !canViewGroup}
									onChange={(newUsers) => dispatch(setOwnerIds(newUsers))}
									returnValue='id'
								/>
								<FormHelperText>&nbsp;</FormHelperText>
							</FormControl>
						</Grid>
						<Grid {...generalGridProps}>
							<FormControl fullWidth>
								<FormLabel>Broker Group</FormLabel>
								<BrokerGroupSelect
									showCheckbox
									brokerGroupId={
										filters?.brokerGroupId ?? userDetails?.brokerGroupId
									}
									disabled={isInitialLoading || !canViewGroup}
									onChange={(newBrokerGroup) =>
										dispatch(setBrokerGroupId(newBrokerGroup))
									}
									disableClearable={true}
								/>
								<FormHelperText>&nbsp;</FormHelperText>
							</FormControl>
						</Grid>
					</>
				)}
				<Grid {...clearFilterGridProps}>
					<Tooltip title='Clear filters'>
						<IconButton
							variant='text'
							size='small'
							color='primary'
							sx={{
								mb: 3,
							}}
							onClick={() => {
								handleReset();
								dispatch(resetFilters());
							}}
						>
							<FilterAltOffOutlinedIcon />
						</IconButton>
					</Tooltip>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default InsightContactsTableFilters;
